import React from 'react';
import {
  TouchableOpacity,
  Text,
  StyleSheet,
  Platform,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import { colors, fontSize, fontWeight, spacing } from '../../theme';

export type FormCompactNeutralButtonProps = {
  onPress: () => void;
  accessibilityLabel: string;
  disabled?: boolean;
  testID?: string;
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  /** Stretch to the parent width with centered label — assist footer Insert. */
  fullWidth?: boolean;
};

/**
 * Compact Neutral control (light grey fill, dark label) — Figma Enabled Button → Neutral.
 * Do not use Primary orange for secondary insert actions.
 */
export const FormCompactNeutralButton: React.FC<
  FormCompactNeutralButtonProps
> = ({
  onPress,
  accessibilityLabel,
  disabled = false,
  testID,
  children,
  style,
  fullWidth = false,
}) => (
  <TouchableOpacity
    style={[
      styles.button,
      fullWidth && styles.buttonFullWidth,
      disabled && styles.buttonDisabled,
      style,
    ]}
    onPress={onPress}
    disabled={disabled}
    accessibilityRole="button"
    accessibilityLabel={accessibilityLabel}
    accessibilityState={{ disabled }}
    activeOpacity={disabled ? 1 : 0.88}
    testID={testID}
  >
    <Text style={[styles.text, fullWidth && styles.textCentered]}>
      {children}
    </Text>
  </TouchableOpacity>
);

const styles = StyleSheet.create({
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    flexShrink: 0,
    backgroundColor: colors.backgroundSecondary,
    borderWidth: 1,
    borderColor: colors.borderMedium,
    borderStyle: 'solid',
    paddingVertical: spacing.formCompactActionPaddingVertical,
    paddingHorizontal: spacing.formCompactActionPaddingHorizontal,
    borderRadius: spacing.cardBorderRadius,
    ...Platform.select({
      web: {
        /**
         * RN Web maps `TouchableOpacity` to `<button>`. Primary zeros the UA stroke because
         * orange fill is enough; Neutral fill matches the example callout, so keep the 1px
         * `borderMedium` outline or Insert reads as plain text.
         */
        borderStyle: 'solid' as const,
        borderWidth: 1,
        borderColor: colors.borderMedium,
        outlineWidth: 0,
        outlineStyle: 'none' as const,
        boxShadow: 'none' as const,
        appearance: 'none' as const,
        WebkitAppearance: 'none' as const,
        cursor: 'pointer' as const,
      },
      default: {},
    }),
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  buttonFullWidth: {
    alignSelf: 'stretch',
    width: '100%',
    justifyContent: 'center',
  },
  text: {
    color: colors.textPrimary,
    fontWeight: fontWeight.semibold,
    fontSize: fontSize.body,
  },
  textCentered: {
    textAlign: 'center',
    width: '100%',
  },
});
